import { useEffect, useMemo, useState } from "react";
import { Loader2, Calendar as CalendarIcon, Clock, Check, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { trackEvent } from "@/lib/analytics";
import { useLang } from "@/context/LanguageContext";
import { validatePersonName, validateMessageText, PHONE_PATTERN } from "@/lib/validation";

function localDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const icsDateFmt = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

function buildIcsBlob({ id, start_time, end_time }) {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Eazy-Travels//Appointments//EN",
    "BEGIN:VEVENT",
    `UID:${id}@eazy-travels.com`,
    `DTSTAMP:${icsDateFmt(new Date().toISOString())}`,
    `DTSTART:${icsDateFmt(start_time)}`,
    `DTEND:${icsDateFmt(end_time)}`,
    "SUMMARY:Eazy-Travels Consultation",
    "END:VEVENT",
    "END:VCALENDAR",
    "",
  ].join("\r\n");
  return new Blob([lines], { type: "text/calendar" });
}

// Google's own "quick add" URL scheme — opens Google Calendar pre-filled and saves
// directly with one click, no file download/import needed (what Gmail/Google Calendar
// users actually expect from "add to calendar").
function buildGoogleCalendarUrl({ start_time, end_time }) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: "Eazy-Travels Consultation",
    dates: `${icsDateFmt(start_time)}/${icsDateFmt(end_time)}`,
    details: "Your free consultation with Eazy-Travels. Need to reschedule or cancel? Use the link from your confirmation email.",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function AppointmentScheduler() {
  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", meeting_preference: "phone", website: "" });

  useEffect(() => {
    let cancelled = false;
    api.get("/availability/slots").then(({ data }) => {
      if (cancelled) return;
      setSlots(data.slots || []);
    }).catch(() => {
      toast.error(t("scheduler.loadError"));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slotsByDate = useMemo(() => {
    const map = new Map();
    for (const iso of slots) {
      const d = new Date(iso);
      const key = localDateKey(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(iso);
    }
    for (const list of map.values()) list.sort();
    return map;
  }, [slots]);

  const availableDateKeys = useMemo(() => new Set(slotsByDate.keys()), [slotsByDate]);
  const dateBounds = useMemo(() => {
    if (slots.length === 0) return null;
    return { from: new Date(slots[0]), to: new Date(slots[slots.length - 1]) };
  }, [slots]);

  const timesForSelectedDate = selectedDate ? (slotsByDate.get(localDateKey(selectedDate)) || []) : [];

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (form.website.trim()) return; // honeypot filled — a bot, not a person; drop silently
    const nameError = validatePersonName(form.name);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    if (form.meeting_preference === "phone" && !form.phone.trim()) {
      toast.error(t("scheduler.phoneRequired"));
      return;
    }
    if (form.phone.trim() && !PHONE_PATTERN.test(form.phone.trim())) {
      toast.error(t("contact.phoneInvalid") || "Please enter a valid phone number (digits only).");
      return;
    }
    const messageError = validateMessageText(form.message);
    if (messageError) {
      toast.error(messageError);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", {
        ...form,
        start_time: selectedSlot,
        client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      trackEvent("generate_lead", { method: "appointment" });
      setBooked(data);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("You're submitting too quickly — please wait a moment and try again.");
        return;
      }
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("scheduler.bookingFailed"));
      if (err.response?.status === 409) {
        // Slot was taken concurrently — refresh the list and back out to the picker.
        setSelectedSlot(null);
        api.get("/availability/slots").then(({ data }) => setSlots(data.slots || []));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function downloadIcs() {
    const blob = buildIcsBlob(booked);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "eazy-travels-consultation.ics";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return (
      <div className="h-64 grid place-items-center">
        <Loader2 size={28} className="animate-spin text-[var(--brand-orange)]" />
      </div>
    );
  }

  if (booked) {
    return (
      <div className="p-6 text-center space-y-4" data-testid="appointment-confirmed">
        <div className="w-14 h-14 mx-auto grid place-items-center rounded-full bg-[var(--gold)]/15 border border-[var(--gold)]/30">
          <Check size={24} className="text-[var(--gold)]" />
        </div>
        <h3 className="font-display text-xl font-bold text-white">{t("scheduler.booked")}</h3>
        <p className="text-white/65">
          {new Date(booked.start_time).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
        </p>
        <p className="text-white/50 text-sm">{t("scheduler.confirmationSent")} {form.email}.</p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center pt-1">
          <a
            href={buildGoogleCalendarUrl(booked)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold text-sm py-2 px-4"
            data-testid="appointment-google-calendar"
          >
            <CalendarIcon size={14} /> {t("scheduler.addToGoogleCalendar")}
          </a>
          <button onClick={downloadIcs} className="btn-outline-gold text-sm py-2 px-4" data-testid="appointment-add-to-calendar">
            <Download size={14} /> {t("scheduler.downloadIcs")}
          </button>
        </div>
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="p-6 text-center text-white/60" data-testid="appointment-no-slots">
        {t("scheduler.noSlots")}
      </div>
    );
  }

  if (selectedSlot) {
    return (
      <form onSubmit={submit} className="p-6 space-y-4" data-testid="appointment-form">
        <button
          type="button"
          onClick={() => setSelectedSlot(null)}
          className="text-xs text-white/50 hover:text-[var(--gold)] inline-flex items-center gap-1"
        >
          <ArrowLeft size={12} /> {t("scheduler.changeTime")}
        </button>
        <p className="text-white font-medium">
          {new Date(selectedSlot).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
        </p>

        {/* Honeypot: invisible to real visitors, off-screen (not display:none, which some
            bots detect and skip) and out of tab order — bots that auto-fill every field trip it. */}
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
          <label htmlFor="appointment-website">Website</label>
          <input
            id="appointment-website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={upd("website")}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("scheduler.fullName")}</label>
            <input required value={form.name} onChange={upd("name")} className="input-dark" data-testid="appointment-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.email")}</label>
            <input required type="email" value={form.email} onChange={upd("email")} className="input-dark" data-testid="appointment-email" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">
              {t("contact.phone")} {form.meeting_preference === "phone" && "*"}
            </label>
            <input
              required={form.meeting_preference === "phone"}
              type="tel"
              inputMode="tel"
              pattern="[0-9+()\-.\s]{6,30}"
              maxLength={30}
              value={form.phone}
              onChange={upd("phone")}
              className="input-dark"
              data-testid="appointment-phone"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("scheduler.reachYou")}</label>
            <select value={form.meeting_preference} onChange={upd("meeting_preference")} className="input-dark" data-testid="appointment-preference">
              <option value="phone">{t("scheduler.phoneCall")}</option>
              <option value="video">{t("scheduler.videoCall")}</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("scheduler.notesOptional")}</label>
          <textarea value={form.message} onChange={upd("message")} rows={3} className="input-dark" data-testid="appointment-message" />
        </div>

        <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="appointment-submit">
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          {t("scheduler.confirmBooking")}
        </button>
      </form>
    );
  }

  return (
    <div className="p-6" data-testid="appointment-picker">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="flex flex-col items-center">
          <p className="text-xs uppercase tracking-widest text-white/55 mb-2 self-start flex items-center gap-1.5">
            <CalendarIcon size={13} /> {t("scheduler.pickDate")}
          </p>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            startMonth={dateBounds?.from}
            endMonth={dateBounds?.to}
            disabled={(date) => !availableDateKeys.has(localDateKey(date))}
            className="bg-white/5 rounded-xl border border-white/10"
          />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-white/55 mb-2 flex items-center gap-1.5">
            <Clock size={13} /> {t("scheduler.pickTime")}
          </p>
          {!selectedDate && <p className="text-white/40 text-sm">{t("scheduler.selectDatePrompt")}</p>}
          {selectedDate && (
            <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
              {timesForSelectedDate.map((iso) => (
                <button
                  key={iso}
                  type="button"
                  onClick={() => setSelectedSlot(iso)}
                  className="text-sm py-2 rounded-lg border border-white/10 text-white/75 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 transition"
                  data-testid={`appointment-slot-${iso}`}
                >
                  {new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
